import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import baseURL from "../../../utils/baseURL";
import {
  resetErrAction,
  resetSuccessAction,
} from "../globalActions/globalActions";

//initialState
const initialState = {
  loading: false,
  error: null,
  users: [],
  user: null,
  profile: {},
  userAuth: {
    loading: false,
    error: null,
    userInfo: localStorage.getItem("userInfo")
      ? JSON.parse(localStorage.getItem("userInfo"))
      : null,
  },
  passwordUpdate: {
    loading: false,
    success: false,
    error: null,
  },
};

// Actions

// Register User
export const registerUserAction = createAsyncThunk(
  "users/register",
  async ({ email, password, fullname }, { rejectWithValue }) => {
    try {
      const { data } = await axios.post(`${baseURL}/users/register`, {
        email,
        password,
        fullname,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// Update User Shipping Address
export const updateUserShippingAddressAction = createAsyncThunk(
  "users/update-shipping-address",
  async (
    { firstName, lastName, address, city, postalCode, province, phone, country },
    { rejectWithValue, getState }
  ) => {
    try {
      const token = getState()?.users?.userAuth?.userInfo?.token;
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const { data } = await axios.put(
        `${baseURL}/users/update/shipping`,
        { firstName, lastName, address, city, postalCode, province, phone, country },
        config
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// Fetch User Profile
export const getUserProfileAction = createAsyncThunk(
  "users/profile-fetched",
  async (_, { rejectWithValue, getState }) => {
    try {
      const token = getState()?.users?.userAuth?.userInfo?.token;
      console.log("Getting user profile with token:", token ? "Token present" : "No token");

      if (!token) {
        return rejectWithValue({
          message: "No authentication token found. Please login again."
        });
      }

      const config = { headers: { Authorization: `Bearer ${token}` } };
      console.log("Making request to:", `${baseURL}/users/profile`);

      const { data } = await axios.get(`${baseURL}/users/profile`, config);
      console.log("Profile data received:", data);
      return data;
    } catch (error) {
      console.log("Error in getUserProfileAction:", error);
      console.log("Error response:", error?.response?.data);
      console.log("Error status:", error?.response?.status);

      // Handle different error scenarios
      if (error?.response?.status === 401) {
        return rejectWithValue({
          message: "Your session has expired. Please login again."
        });
      }

      return rejectWithValue(error?.response?.data || {
        message: "Failed to fetch user profile"
      });
    }
  }
);

// Login User
export const loginUserAction = createAsyncThunk(
  "users/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      console.log(`${baseURL}/users/login`);
      console.log(email)
      console.log(password)
      const { data } = await axios.post(`${baseURL}/users/login`, { email, password });
      localStorage.setItem("userInfo", JSON.stringify(data));
      console.log(data)
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// Logout User
export const logoutAction = createAsyncThunk("users/logout", async () => {
  localStorage.removeItem("userInfo");
  return true;
});

// Change Password
export const changePasswordAction = createAsyncThunk(
  "users/change-password",
  async ({ currentPassword, newPassword }, { rejectWithValue, getState }) => {
    try {
      const token = getState()?.users?.userAuth?.userInfo?.token;
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };
      
      const { data } = await axios.post(
        `${baseURL}/users/change-password`,
        { currentPassword, newPassword },
        config
      );
      return data;
    } catch (error) {
      return rejectWithValue(error?.response?.data);
    }
  }
);

// Slice
const usersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    // Reset error and loading states
    resetUserError: (state) => {
      state.error = null;
    },
    resetUserLoading: (state) => {
      state.loading = false;
    },
    resetPasswordState: (state) => {
      state.passwordUpdate = {
        loading: false,
        success: false,
        error: null,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(loginUserAction.pending, (state) => {
        state.userAuth.loading = true;
      })
      .addCase(loginUserAction.fulfilled, (state, action) => {
        state.userAuth.userInfo = action.payload;
        state.userAuth.loading = false;
      })
      .addCase(loginUserAction.rejected, (state, action) => {
        state.userAuth.error = action.payload;
        state.userAuth.loading = false;
      })
      // Register
      .addCase(registerUserAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(registerUserAction.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(registerUserAction.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Logout
      .addCase(logoutAction.fulfilled, (state) => {
        state.userAuth.userInfo = null;
      })
      // Profile
      .addCase(getUserProfileAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(getUserProfileAction.fulfilled, (state, action) => {
        state.profile = action.payload;
        state.loading = false;
      })
      .addCase(getUserProfileAction.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;

        // If the error is related to authentication, clear the user info
        if (action.payload?.message?.includes("session has expired") ||
            action.payload?.message?.includes("authentication") ||
            action.payload?.message?.includes("login again")) {
          state.userAuth.userInfo = null;
          localStorage.removeItem("userInfo");
        }
      })
      // Shipping Address
      .addCase(updateUserShippingAddressAction.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateUserShippingAddressAction.fulfilled, (state, action) => {
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(updateUserShippingAddressAction.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      })
      // Reset Error Action
      .addCase(resetErrAction.pending, (state) => {
        state.error = null;
      })
      // Change Password
      .addCase(changePasswordAction.pending, (state) => {
        state.passwordUpdate.loading = true;
        state.passwordUpdate.error = null;
      })
      .addCase(changePasswordAction.fulfilled, (state) => {
        state.passwordUpdate.loading = false;
        state.passwordUpdate.success = true;
        state.passwordUpdate.error = null;
      })
      .addCase(changePasswordAction.rejected, (state, action) => {
        state.passwordUpdate.loading = false;
        state.passwordUpdate.success = false;
        state.passwordUpdate.error = action.payload;
      });
  },
});

// Export actions and reducer
export const { resetUserError, resetUserLoading, resetPasswordState } = usersSlice.actions;
export default usersSlice.reducer;
